import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import MuiAlert from "@mui/material/Alert";
import moment from "moment";
import { httpclient } from "../../../../utils/httpClient";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Header = styled("div")(({ theme }) => ({
  "& h1": {
    color: theme.palette.primary.dark,
    margin: "0",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.light,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const columns = [
  { id: "marketplace_id", name: "Marketplace ID" },
  { id: "marketplace", name: "Marketplace Name" },
  { id: "updated_date", name: "Last Action Date" },
  { id: "push_soh", name: "Action" },
];

const SOHPush = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pushingID, setPushingID] = useState(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");

  useEffect(() => {
    getSohPushRecords();
  }, []);

  const getSohPushRecords = () => {
    setLoading(true);
    httpclient
      .get(`netsuite/v1/getSohPushRecords`)
      .then(({ data }) => {
        if (data.success) {
          setRows(data.records);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handlePushSOH = (marketplaceID) => {
    setPushingID(marketplaceID);
    httpclient
      .post(`netsuite/v1/marketplace-soh-update/${marketplaceID}`)
      .then(({ data }) => {
        setPushingID(null);
        setOpen(true);
        if (data.status === true) {
          setMessageState("success");
          setMessage(data.message || "SOH push triggered successfully.");
          getSohPushRecords();
        } else {
          setMessageState("error");
          setMessage(data.message || "Failed to trigger SOH push.");
        }
      })
      .catch((err) => {
        setPushingID(null);
        setOpen(true);
        setMessageState("error");
        setMessage(
          err?.response?.data?.message || "An error occurred while triggering SOH push."
        );
      });
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  return (
    <div>
      <Header>
        <h1>Trigger Marketplace SOH Push</h1>
      </Header>

      <Box mt={2}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table sx={{ minWidth: 700 }} aria-label="soh-push-table">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <StyledTableCell key={col.id}>{col.name}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>{row.marketplace_id}</StyledTableCell>
                      <StyledTableCell>{row.marketplace}</StyledTableCell>
                      <StyledTableCell>
                        {row.updated_date
                          ? moment(row.updated_date).format(
                              "ddd, MMM Do YYYY, h:mm:ss a"
                            )
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        {pushingID === row.marketplace_id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handlePushSOH(row.marketplace_id)}
                          >
                            Push SOH
                          </Button>
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Snackbar
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        onClose={handleCloseSnack}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={messageState}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SOHPush;
